import React from 'react';
import { observer } from 'mobx-react';
import type { FileResponseItem } from '../models.ts';
import type { UploadItem } from '@services/uploadService.ts';
import { fileService } from '@services/fileService.ts';
import { uploadService } from '@services/uploadService.ts';
import { Timestamp } from '@components/Timestamp.tsx';
import { FileCardMenu } from '@components/FileCardMenu.tsx';
import { FileIcon } from '@components/FileIcon.tsx';
import { ImgShare } from '@components/Images/ImgShare.tsx';
import { ImgClose } from '@components/Images/ImgClose.tsx';
import { getPreviewableType, getExpirationWarning } from '@root/utils/fileUtils.ts';

type FileGridViewProps = {
  files: FileResponseItem[];
  activeUploads: UploadItem[];
  removingIds: Set<string>;
  renderText: (text: string | undefined | null) => React.ReactNode;
  onPreviewFile: (file: FileResponseItem) => void;
  onDeleteFile: (file: FileResponseItem) => void;
  onPermanentDelete: (file: FileResponseItem) => void;
  onManageExpiration: (file: FileResponseItem) => void;
  onCustomize: (file: FileResponseItem) => void;
  onShareCode: (file: FileResponseItem) => void;
  onReupload?: (file: FileResponseItem) => void;
  onCustomLink?: (file: FileResponseItem) => void;
};

export const FileGridView = observer(function FileGridView({
  files,
  activeUploads,
  removingIds,
  renderText,
  onPreviewFile,
  onDeleteFile,
  onPermanentDelete,
  onManageExpiration,
  onCustomize,
  onShareCode,
  onReupload,
  onCustomLink,
}: FileGridViewProps) {
  return (
    <div className="file-grid">
      {activeUploads.map(upload => (
        <div className="file-card file-card-uploading" key={'up-' + upload.id}>
          <div className="file-actions">
            {upload.status !== 'registering' && (
              <a
                className="action-btn"
                href={`/file/download/${upload.id}`}
                target="_blank"
                rel="noopener noreferrer"
                title="Open share page"
              >
                <ImgShare size={16} />
              </a>
            )}
            {(upload.status === 'uploading' || upload.status === 'registering') && (
              <button className="action-btn" title="Cancel upload" onClick={() => uploadService.cancelUpload(upload.id)}><ImgClose /></button>
            )}
          </div>
          <div className="file-preview"><FileIcon fileName={upload.fileName} /></div>
          <div className="file-name">{upload.fileName}</div>
          {upload.status === 'registering' ? (
            <>
              <div className="file-meta"><span style={{ opacity: 0.6 }}>Registering on server...</span></div>
              <div className="upload-card-progress-bar">
                <div className="upload-card-progress-fill upload-card-progress-indeterminate" />
              </div>
            </>
          ) : (
            <>
              <div className="file-meta">
                <span>{fileService.formatFileSize(upload.loaded)} / {fileService.formatFileSize(upload.fileSize)}</span>
                {!!upload.bitrate && <span>{uploadService.formatBitrate(upload.bitrate)}</span>}
                {!!upload.timeLeftSeconds && <span>{uploadService.formatTimeLeft(upload.timeLeftSeconds)} left</span>}
              </div>
              <div className="upload-card-progress-bar">
                <div className="upload-card-progress-fill" style={{ width: `${upload.progress}%` }} />
              </div>
            </>
          )}
        </div>
      ))}
      {files.map(file => {
        const fileWarning = getExpirationWarning(file);
        const incompletePct = file.fileSize > 0 ? Math.round(((file.readySize || 0) / file.fileSize) * 100) : 0;
        const previewType = getPreviewableType(file.fileName);
        return (
          <div
            className={`file-card${removingIds.has(file.id) ? ' removing' : ''}${!file.isComplete ? ' file-card-uploading' : ''}`}
            key={'g' + file.id}
            onClick={previewType && file.isComplete ? () => onPreviewFile(file) : undefined}
            style={previewType && file.isComplete ? { cursor: 'pointer' } : undefined}
          >
            <FileCardMenu
              file={file}
              wrapperClassName="file-actions"
              onDelete={() => onDeleteFile(file)}
              onPermanentDelete={() => onPermanentDelete(file)}
              onManageExpiration={() => onManageExpiration(file)}
              onCustomize={() => onCustomize(file)}
              onShareCode={() => onShareCode(file)}
              onReupload={onReupload ? () => onReupload(file) : undefined}
              onCustomLink={onCustomLink ? () => onCustomLink(file) : undefined}
            />
            <div className="file-preview">
              <FileIcon fileName={file.fileName} fileId={file.id} file={file} view="grid" />
            </div>
            <div className="file-name">
              {renderText(file.fileName)}
              {fileWarning && <span title={fileWarning} style={{ color: 'var(--warning, #f59e0b)', marginLeft: '4px', cursor: 'help' }}>*</span>}
            </div>
            <div className="file-meta">
              <span>
                {!file.isComplete
                  ? `${fileService.formatFileSize(file.readySize || 0)} / ${fileService.formatFileSize(file.fileSize)}`
                  : (file.zipFullSize ?? 0) > 0
                    ? fileService.formatFileSize(file.zipFullSize)
                    : fileService.formatFileSize(file.fileSize)}
              </span>
              {(file.zipTotalFiles ?? 0) > 0 ? <span>{file.zipTotalFiles} files</span> : null}
              {file.dateTime && <Timestamp timestamp={file.dateTime} short />}
            </div>
            {!file.isComplete && (
              <>
                <div className="upload-card-progress-bar">
                  <div className="upload-card-progress-fill" style={{ width: `${incompletePct}%` }} />
                </div>
                <div className="file-meta" style={{ marginTop: '4px', color: 'var(--warning-color)' }}>
                  <span>Incomplete download ({incompletePct}%)</span>
                </div>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
});
